import { Alert } from "@cvpartner/design-system";
import { LinkExternal01 } from "@cvpartner/design-system/icons/line";
import { useLocalStorage } from "@uidotdev/usehooks";
import * as React from "react";
import { type Breadcrumb, Breadcrumbs } from "@/components/shared/Breadcrumbs";
import { I18n } from "@/util/translations";
import {
  alertWrapper,
  checkboxWrapper,
  copyright,
  footer,
  list,
  listItemLink,
  loginButtonWrapper,
  loginLinkWrapper,
  outerWrapper,
  supportText,
  wrapper,
} from "./Modern.css";
import { LoginCard } from "./modern/LoginCard";
import { Logo } from "./modern/Logo";

const Footer: React.FC = () => {
  const thisYear = new Date().getFullYear();

  return (
    <footer className={footer}>
      <div className={copyright}>{thisYear} &copy; Flowcase</div>
    </footer>
  );
};

const LoginAlert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={alertWrapper}>{children}</div>
);

// We on purpose ignores the props here as we don't want its size
const FlowcaseIcon: React.FC = () => (
  <svg fill="none" width="40" height="40" viewBox="0 0 40 40">
    <rect width="40" height="40" fill="#F5F7FF" rx="8" />
    <path
      fill="#5B53FF"
      d="M29.9942 12.3581V10H19.1613c-1.8462 0-3.343 1.7465-3.343 3.9008v4.9405H13.343c-1.8462 0-3.343 1.7465-3.343 3.9008V30h2.6383v-8.5695l.1922-.2242H29.039v-2.3718H18.4508v-6.2521l.1922-.2243H30h-.0058Z"
    />
  </svg>
);

const RebrandInfo: React.FC = () => {
  const [showIcon, setShowIcon] = useLocalStorage("showRebrandBanner", true);
  const onClose = React.useCallback(() => {
    setShowIcon(false);
  }, [setShowIcon]);

  if (!showIcon) {
    return null;
  }

  return (
    <Alert
      type="message"
      title={I18n.t("signin.alert.new_name")}
      description={I18n.t("signin.alert.signin_msg_new_name")}
      onClose={onClose}
      icon={FlowcaseIcon}
      actions={[
        {
          displayText: I18n.t("signin.alert.bt_new_name_article"),
          link: "https://www.flowcase.com/blog/cv-partner-is-now-flowcase",
          trailingIcon: LinkExternal01,
        },
      ]}
    />
  );
};

export const Wrapper: React.FC<{
  heading: string;
  subheading?: string | readonly string[];
  rebrandInfo?: boolean;
  alert?: React.ReactNode;
  breadcrumbs?: readonly Breadcrumb[];
  hideLogo?: boolean;
  children?: React.ReactNode;
}> = ({
  heading,
  subheading,
  rebrandInfo,
  alert,
  breadcrumbs,
  children,
  hideLogo = false,
}) => (
  <>
    {breadcrumbs && <Breadcrumbs breadcrumbs_tree={breadcrumbs} />}
    <div className={outerWrapper}>
      <div className={wrapper}>
        <LoginAlert>
          {alert}
          {rebrandInfo && <RebrandInfo />}
        </LoginAlert>
        {!hideLogo && <Logo />}
        <main>
          <LoginCard heading={heading} subheading={subheading}>
            {children}
          </LoginCard>
        </main>
        <Footer />
      </div>
    </div>
  </>
);

export const List: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul className={list}>{children}</ul>
);

export const LoginButtonWrapper: React.FC<{
  center: boolean;
  children: React.ReactNode;
}> = ({ center, children }) => (
  <div className={loginButtonWrapper({ center })}>{children}</div>
);

export const LoginLinkWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => <div className={loginLinkWrapper}>{children}</div>;

export const CheckboxWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => <div className={checkboxWrapper}>{children}</div>;

export const SupportText: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => <p className={supportText}>{children}</p>;

export const ListItemLink: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => <li className={listItemLink}>{children}</li>;
